import React, { useState, useEffect, useRef } from "react";
import { backend_url } from "../App";
import { uploadProfilePicture, deleteProfilePicture } from "../common/api/api";

// Decode JWT expiry without a library
const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch { return true; }
};

const LoginSignup = () => {

  const [state, setState] = useState("Login");
  const [formData, setFormData] = useState({
    username: "", email: "", password: "",
    mobile: "", address: "", city: "", postalCode: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Profile picture
  const [profilePic, setProfilePic] = useState(null); // { url, publicId }
  const [profileLoading, setProfileLoading] = useState(false);
  const fileRef = useRef();

  // Auto-redirect if already logged in with a valid (non-expired) token
  useEffect(() => {
    const token = localStorage.getItem('auth-token');
    if (token && !isTokenExpired(token)) {
      window.location.replace("/");
    } else if (token) {
      // Token exists but expired — remove it
      localStorage.removeItem('auth-token');
    }
  }, []);

  const changeHandler = (e) => {
    setError("");
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleProfileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfileLoading(true);
    const fd = new FormData();
    fd.append("profile", file);
    try {
      const data = await uploadProfilePicture(fd);
      if (data.success) setProfilePic({ url: data.url, publicId: data.publicId });
    } catch {}
    setProfileLoading(false);
    e.target.value = "";
  };

  const handleDeleteProfile = async () => {
    if (!profilePic || profileLoading) return;
    setProfileLoading(true);
    try { await deleteProfilePicture(profilePic.publicId); } catch {}
    setProfilePic(null);
    setProfileLoading(false);
  };

  const validate = () => {
    if (state === "Sign Up" && !formData.username.trim()) {
      setError("Please enter your full name"); return false;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address"); return false;
    }
    if (!formData.password) {
      setError("Please enter a password"); return false;
    }
    if (state === "Sign Up" && formData.password.length < 6) {
      setError("Password must be at least 6 characters"); return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    setError("");
    try {
      const url = state === "Login" ? `${backend_url}/login` : `${backend_url}/signup`;
      const body = state === "Login"
        ? { email: formData.email, password: formData.password }
        : {
            ...formData,
            profilePicture: profilePic?.url || "",
            profilePicturePublicId: profilePic?.publicId || "",
          };
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const dataObj = await resp.json();
      if (dataObj.success) {
        localStorage.setItem('auth-token', dataObj.token);
        localStorage.setItem('user-role', dataObj.role || 'user');
        localStorage.setItem('user-name', dataObj.name || '');
        window.location.replace("/");
      } else {
        setError(dataObj.errors || "Something went wrong. Please try again.");
      }
    } catch {
      setError("Unable to connect to the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  const switchTab = (tab) => {
    setState(tab);
    setError("");
    setFormData({ username: "", email: "", password: "", mobile: "", address: "", city: "", postalCode: "" });
    setShowPassword(false);
    setProfilePic(null);
  };

  const inputStyle = { border: '1px solid rgba(0,212,255,0.2)', borderRadius: '10px', background: 'rgba(0,212,255,0.05)' };
  const inputClass = "h-[50px] px-4 text-white text-[15px] font-[inherit] outline-none transition-all duration-300 placeholder:text-[#64748b] w-full";
  const onFocus = (e) => { e.target.style.borderColor = '#00d4ff'; e.target.style.background = 'rgba(0,212,255,0.1)'; };
  const onBlur = (e) => { e.target.style.borderColor = 'rgba(0,212,255,0.2)'; e.target.style.background = 'rgba(0,212,255,0.05)'; };

  const isSignUp = state === "Sign Up";

  return (
    <div className="w-full min-h-screen relative flex items-center justify-center py-6 px-4 sm:py-10 sm:px-5 overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(6,12,24,0.95) 0%, rgba(12,24,48,0.95) 100%)' }}>
      {/* Glow orbs */}
      <div className="absolute w-[320px] h-[320px] sm:w-[400px] sm:h-[400px] -top-20 sm:-top-24 -left-20 sm:-left-24 rounded-full blur-[80px] opacity-30 pointer-events-none" style={{ background: 'radial-gradient(circle, #00d4ff, transparent)', animation: 'login-float 15s infinite ease-in-out' }} />
      <div className="absolute w-[280px] h-[280px] sm:w-[350px] sm:h-[350px] -bottom-8 sm:-bottom-12 -right-8 sm:-right-12 rounded-full blur-[80px] opacity-30 pointer-events-none" style={{ background: 'radial-gradient(circle, #7c3aed, transparent)', animation: 'login-float 15s infinite ease-in-out 5s' }} />
      {/* Grid */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(0,212,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.05) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />

      <div className="relative z-10 w-full rounded-[16px] sm:rounded-[20px] shadow-[0_20px_60px_rgba(0,0,0,0.5)] animate-slide-up" style={{ background: 'rgba(6,12,24,0.8)', backdropFilter: 'blur(20px)', border: '1px solid rgba(0,212,255,0.15)', padding: '24px 16px', maxWidth: isSignUp ? '520px' : '420px' }}>

        {/* Header */}
        <div className="mb-6 sm:mb-8 text-center">
          <h1 className="text-[28px] sm:text-3xl font-bold mb-2 sm:mb-3 bg-gradient-to-br from-[#00d4ff] to-[#7c3aed] bg-clip-text text-transparent -tracking-[0.5px]">
            {isSignUp ? "Create Account" : "Welcome Back"}
          </h1>
          <p className="text-[#a0aec0] text-[13px] sm:text-sm leading-[1.6]">
            {isSignUp ? "Create your account to start shopping premium gadgets" : "Sign in to access your account and explore amazing tech deals"}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 sm:gap-3 mb-6 sm:mb-7 rounded-xl p-1.5" style={{ background: 'rgba(0,212,255,0.05)' }}>
          {["Login", "Sign Up"].map(tab => (
            <button
              key={tab}
              className={`flex-1 py-2.5 px-3 sm:px-4 border-none text-[13px] sm:text-sm font-semibold cursor-pointer rounded-[10px] transition-all duration-300 font-[inherit] ${state === tab ? 'text-[#00d4ff]' : 'bg-transparent text-[#a0aec0] hover:text-[#00d4ff]'}`}
              style={state === tab ? { background: 'linear-gradient(135deg, rgba(0,212,255,0.2), rgba(124,58,237,0.2))', boxShadow: '0 0 20px rgba(0,212,255,0.2)' } : {}}
              onClick={() => switchTab(tab)}
            >{tab}</button>
          ))}
        </div>

        {/* Profile picture upload — Sign Up only */}
        {isSignUp && (
          <div className="flex justify-center mb-5 sm:mb-6">
            <div className="relative">
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleProfileSelect} />

              {/* Circle */}
              <div
                onClick={() => !profileLoading && fileRef.current.click()}
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center overflow-hidden cursor-pointer transition-all duration-300"
                style={{
                  border: profilePic ? '2px solid rgba(0,212,255,0.6)' : '2px dashed rgba(0,212,255,0.3)',
                  background: profilePic ? 'transparent' : 'rgba(0,212,255,0.05)',
                  boxShadow: profilePic ? '0 0 20px rgba(0,212,255,0.25)' : 'none',
                }}
              >
                {profileLoading ? (
                  <svg className="animate-spin" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00d4ff" strokeWidth="2.5">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                ) : profilePic ? (
                  <img src={profilePic.url} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="rgba(0,212,255,0.5)" strokeWidth="1.5">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                      <circle cx="12" cy="13" r="4"/>
                    </svg>
                    <span className="text-[10px] text-[#4a5568]">Upload</span>
                  </div>
                )}
              </div>

              {/* Trash icon — visible when image is set */}
              {profilePic && !profileLoading && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteProfile(); }}
                  className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center cursor-pointer border-none transition-all duration-200 hover:scale-110"
                  style={{ background: 'rgba(239,68,68,0.9)', boxShadow: '0 2px 8px rgba(239,68,68,0.4)' }}
                  title="Remove photo"
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14H6L5 6"/>
                    <path d="M10 11v6M14 11v6"/>
                    <path d="M9 6V4h6v2"/>
                  </svg>
                </button>
              )}

              <p className="text-center text-[11px] text-[#4a5568] mt-2">
                {profilePic ? "Click to change" : "Profile photo"}
              </p>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-4 px-4 py-3 rounded-[10px] flex items-center gap-2.5 text-sm font-medium text-red-400" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="shrink-0">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {error}
          </div>
        )}

        {/* Fields */}
        <div className="flex flex-col gap-3 sm:gap-4 mb-6">
          {isSignUp && (
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-[#a0aec0] uppercase tracking-[0.5px]">Full Name</label>
              <input type="text" placeholder="Enter your full name" name="username" value={formData.username}
                onChange={changeHandler} onKeyDown={handleKeyDown} onFocus={onFocus} onBlur={onBlur}
                className={inputClass} style={inputStyle}
              />
            </div>
          )}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold text-[#a0aec0] uppercase tracking-[0.5px]">Email Address</label>
            <input type="email" placeholder="Enter your email" name="email" value={formData.email}
              onChange={changeHandler} onKeyDown={handleKeyDown} onFocus={onFocus} onBlur={onBlur}
              className={inputClass} style={inputStyle}
            />
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-[#a0aec0] uppercase tracking-[0.5px]">Password</label>
              {isSignUp && <span className="text-xs text-[#4a5568]">Min. 6 characters</span>}
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder={isSignUp ? "Create a password" : "Enter your password"}
                name="password" value={formData.password}
                onChange={changeHandler} onKeyDown={handleKeyDown} onFocus={onFocus} onBlur={onBlur}
                className={`${inputClass} pr-12`} style={inputStyle}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4a5568] hover:text-[#a0aec0] transition-colors cursor-pointer"
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Extra Sign Up fields */}
          {isSignUp && (
            <>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-[#a0aec0] uppercase tracking-[0.5px]">Mobile Number</label>
                <input type="tel" placeholder="+92 300 0000000" name="mobile" value={formData.mobile}
                  onChange={changeHandler} onKeyDown={handleKeyDown} onFocus={onFocus} onBlur={onBlur}
                  className={inputClass} style={inputStyle}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-[#a0aec0] uppercase tracking-[0.5px]">Address</label>
                <input type="text" placeholder="Street address" name="address" value={formData.address}
                  onChange={changeHandler} onKeyDown={handleKeyDown} onFocus={onFocus} onBlur={onBlur}
                  className={inputClass} style={inputStyle}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-3">
                <div className="flex flex-col gap-2 flex-1">
                  <label className="text-xs font-semibold text-[#a0aec0] uppercase tracking-[0.5px]">City</label>
                  <input type="text" placeholder="City" name="city" value={formData.city}
                    onChange={changeHandler} onKeyDown={handleKeyDown} onFocus={onFocus} onBlur={onBlur}
                    className={inputClass} style={inputStyle}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-[#a0aec0] uppercase tracking-[0.5px]">Postal Code</label>
                  <input type="text" placeholder="12345" name="postalCode" value={formData.postalCode}
                    onChange={changeHandler} onKeyDown={handleKeyDown} onFocus={onFocus} onBlur={onBlur}
                    className={inputClass} style={inputStyle}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Submit button */}
        <button
          className="w-full h-[50px] border-none rounded-[10px] text-[#060c18] text-[15px] font-bold cursor-pointer flex items-center justify-center gap-2.5 mb-5 tracking-[0.5px] transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed hover:enabled:-translate-y-0.5"
          style={{ background: 'linear-gradient(135deg, #00d4ff, #0099cc)', boxShadow: '0 8px 24px rgba(0,212,255,0.3)' }}
          onClick={handleSubmit}
          disabled={loading || profileLoading}
        >
          {loading ? (
            <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
            </svg>
          ) : (
            <>
              <span>{isSignUp ? "Create Account" : "Sign In"}</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </>
          )}
        </button>

        {/* Switch tab link */}
        <div className="text-center mb-4 sm:mb-5">
          {!isSignUp ? (
            <p className="text-[#a0aec0] text-sm">Don't have an account? <span className="text-[#00d4ff] font-semibold cursor-pointer hover:text-[#7c3aed] transition-colors" onClick={() => switchTab("Sign Up")}>Sign Up</span></p>
          ) : (
            <p className="text-[#a0aec0] text-sm">Already have an account? <span className="text-[#00d4ff] font-semibold cursor-pointer hover:text-[#7c3aed] transition-colors" onClick={() => switchTab("Login")}>Sign In</span></p>
          )}
        </div>

        {/* Session info */}
        <div className="flex items-start sm:items-center gap-2.5 p-3 rounded-[10px]" style={{ background: 'rgba(0,212,255,0.05)' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00d4ff" strokeWidth="2" className="shrink-0 opacity-70">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <span className="text-xs text-[#4a5568]">
            Your session stays active for <span className="text-[#a0aec0] font-medium">30 days</span> — no need to log in every time.
          </span>
        </div>

      </div>
    </div>
  );
};

export default LoginSignup;
